import React from 'react';
import { Upload, UserCheck, FileText, ShieldCheck } from 'lucide-react';
const stages = [{
  number: '01',
  icon: Upload,
  title: 'Instruction & Secure Document Upload',
  description: 'Submit your instruction via our secure, GDPR-compliant portal. Upload case documentation, medical records, and specific legal questions for the expert.',
  timing: 'Immediate acknowledgement',
  iconAlt: 'Icon of a locked folder representing GDPR-compliant medical record transfer'
}, {
  number: '02',
  icon: UserCheck,
  title: 'Expert Triage & Conflict Check',
  description: 'Our clinical lead reviews every case to ensure the correct specialist is assigned. Full conflict-of-interest screening completed before allocation.',
  timing: 'Completed within 24 hours',
  iconAlt: 'Minimalist icon representing expert triage and conflict-of-interest screening'
}, {
  number: '03',
  icon: FileText,
  title: 'Clinical Assessment & Drafting',
  description: 'The allocated consultant conducts the examination and prepares a detailed, evidence-based report addressing all legal questions raised in the instruction.',
  timing: '1–3 weeks depending on complexity',
  iconAlt: 'Document icon representing clinical assessment and medico-legal report drafting'
}, {
  number: '04',
  icon: ShieldCheck,
  title: 'Quality Assurance & Delivery',
  description: 'Every report is audited for clarity, nomenclature accuracy, CPR Part 35 compliance, and adherence to legal instructions before encrypted delivery.',
  timing: 'Final audit before release',
  iconAlt: 'Minimalist shield icon representing clinical governance and legal compliance'
}];
export function ProcessTimeline() {
  return <section id="process" className="py-10 bg-white" aria-label="4-stage clinical governance and triage process for medico-legal reporting" data-id="element-392">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-393">
        <div className="text-center max-w-2xl mx-auto mb-8" data-id="element-394">
          <h2 className="text-3xl md:text-4xl font-bold text-ink-900 tracking-tight leading-tight mb-2" data-id="element-395">
            Triage & Governance
          </h2>
          <p className="text-lg text-ink-500 leading-relaxed" data-id="element-396">
            Every instruction undergoes a rigorous internal audit before it
            reaches your desk.
          </p>
        </div>

        {/* Desktop: horizontal with connecting lines */}
        <div className="hidden md:block" data-id="element-397">
          <div className="relative grid grid-cols-4 gap-6" data-id="element-398">
            {/* Connecting line */}
            <div className="absolute top-[3.25rem] left-[12.5%] right-[12.5%] h-px bg-warm-300 z-0" data-id="element-399" />

            {stages.map(stage => <div key={stage.number} className="relative z-10 flex flex-col items-center text-center" data-id="element-400">
                {/* Number + Icon circle */}
                <div className="relative mb-5" data-id="element-401">
                  <div className="w-[6.5rem] h-[6.5rem] rounded-full bg-warm-50 border-2 border-warm-200 flex items-center justify-center" role="img" aria-label={stage.iconAlt} data-id="element-402">
                    <stage.icon className="h-8 w-8 text-slate-500" aria-hidden="true" data-id="element-403" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-[#2D3748] text-white flex items-center justify-center text-sm font-bold shadow-sm" data-id="element-404">
                    {stage.number}
                  </div>
                </div>

                <h3 className="text-base font-bold text-ink-900 mb-2 leading-tight" data-id="element-405">
                  {stage.title}
                </h3>
                <p className="text-ink-500 leading-relaxed text-sm max-w-[220px] mb-3" data-id="element-406">
                  {stage.description}
                </p>
                <span className="inline-flex items-center text-xs font-semibold text-teal-600 bg-teal-50 px-3 py-1 rounded-full border border-teal-100" data-id="element-407">
                  {stage.timing}
                </span>
              </div>)}
          </div>
        </div>

        {/* Mobile: vertical timeline with connected lines */}
        <div className="md:hidden space-y-0" data-id="element-408">
          {stages.map((stage, index) => <div key={stage.number} className="flex items-start gap-5" data-id="element-409">
              {/* Left: number + line */}
              <div className="flex flex-col items-center flex-shrink-0" data-id="element-410">
                <div className="w-14 h-14 rounded-full bg-warm-50 border-2 border-warm-200 flex items-center justify-center relative" role="img" aria-label={stage.iconAlt} data-id="element-411">
                  <stage.icon className="h-6 w-6 text-slate-500" aria-hidden="true" data-id="element-412" />
                  <div className="absolute -top-1.5 -right-1.5 w-7 h-7 rounded-full bg-[#2D3748] text-white flex items-center justify-center text-xs font-bold" data-id="element-413">
                    {stage.number}
                  </div>
                </div>
                {index < stages.length - 1 && <div className="w-px h-8 bg-warm-300 mt-3" data-id="element-414" />}
              </div>

              {/* Right: content */}
              <div className="pt-2 pb-8" data-id="element-415">
                <h3 className="text-lg font-bold text-ink-900 mb-1.5" data-id="element-416">
                  {stage.title}
                </h3>
                <p className="text-ink-500 leading-relaxed text-[15px] mb-2" data-id="element-417">
                  {stage.description}
                </p>
                <span className="inline-flex items-center text-xs font-semibold text-teal-600 bg-teal-50 px-3 py-1 rounded-full border border-teal-100" data-id="element-418">
                  {stage.timing}
                </span>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}